import { ref, computed } from 'vue'

const MIN_ZOOM = 1
const MAX_ZOOM = 3
const WHEEL_STEP = 0.2
const DEFAULT_ORIGIN = 'center center'

// Zoom and rotation for the image opened in the gallery
export const useImageZoom = imageRef => {
  const scale = ref(1)
  const rotation = ref(0)
  const origin = ref(DEFAULT_ORIGIN)

  const wrapperStyle = computed(() => ({
    transform: `rotate(${rotation.value}deg)`,
  }))

  const imageStyle = computed(() => ({
    transform: `scale(${scale.value})`,
    transformOrigin: origin.value,
    cursor: scale.value < MAX_ZOOM ? 'zoom-in' : 'zoom-out',
  }))

  // Point under the cursor in the coordinates of the unrotated image
  const originAt = (clientX, clientY) => {
    const rect = imageRef.value.getBoundingClientRect()
    const offsetX = clientX - (rect.left + rect.width / 2)
    const offsetY = clientY - (rect.top + rect.height / 2)

    const radians = (-rotation.value * Math.PI) / 180
    const rotatedX = offsetX * Math.cos(radians) - offsetY * Math.sin(radians)
    const rotatedY = offsetX * Math.sin(radians) + offsetY * Math.cos(radians)

    const percent = (value, size) =>
      Math.min(100, Math.max(0, ((value + size / 2) / size) * 100))

    return `${percent(rotatedX, rect.width)}% ${percent(rotatedY, rect.height)}%`
  }

  const zoom = (step, event) => {
    const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, scale.value + step))
    if (next === scale.value) return

    // Zoom towards the cursor, but keep the anchor while already zoomed in
    if (event && scale.value === MIN_ZOOM) {
      origin.value = originAt(event.clientX, event.clientY)
    }
    scale.value = next
  }

  const onWheel = event => {
    zoom(event.deltaY > 0 ? -WHEEL_STEP : WHEEL_STEP, event)
  }

  const onDoubleClick = event => {
    origin.value = originAt(event.clientX, event.clientY)
    scale.value = scale.value >= MAX_ZOOM ? MIN_ZOOM : MAX_ZOOM
  }

  const rotate = direction => {
    rotation.value = (rotation.value + (direction === 'cw' ? 90 : -90)) % 360
    scale.value = MIN_ZOOM
    origin.value = DEFAULT_ORIGIN
  }

  const reset = () => {
    scale.value = MIN_ZOOM
    rotation.value = 0
    origin.value = DEFAULT_ORIGIN
  }

  return {
    rotation,
    wrapperStyle,
    imageStyle,
    zoom,
    onWheel,
    onDoubleClick,
    rotate,
    reset,
  }
}
